import {AfterViewInit, Directive, DoCheck, ElementRef, Host, OnDestroy, Optional, Renderer2, Self} from '@angular/core';
import {combineLatest, distinctUntilChanged, Observable, of, Subject} from 'rxjs';
import {map, startWith, takeUntil} from 'rxjs/operators';
import {NgControl, NgForm} from '@angular/forms';
import {FieldType, FormFieldType} from '~shared/types/form-types';

export enum ValidationStatus {
    VALID = 'VALID',
    INVALID = 'INVALID',
    PENDING = 'PENDING',
    DISABLED = 'DISABLED'
}

@Directive({
    standalone: true,
    selector: '[ngModel][name]:not([formControlName]):not([formControl])'
})
export class ValidationCssDirective implements AfterViewInit, DoCheck, OnDestroy {
    public static readonly CLASS_VALID = 'is-valid';
    public static readonly CLASS_INVALID = 'is-invalid';
    public static readonly ATTRIBUTE_INVALID = 'data-invalid-control';

    protected readonly doCheck$ = new Subject<void>();

    // Form control state
    protected readonly submitted$: Observable<boolean>;
    protected readonly invalid$: Observable<boolean>;
    protected readonly dirty$: Observable<boolean>;

    // Computed hidden state!
    protected readonly visible$: Observable<boolean>;

    protected readonly us$ = new Subject<void>();

    public constructor(
        @Host() @Optional()
        protected readonly ngForm: NgForm | null,
        @Self()
        protected readonly ngControl: NgControl,
        protected readonly elementRef: ElementRef,
        protected readonly renderer: Renderer2
    ) {
        this.submitted$ = this.ngForm
            ? this.doCheck$.pipe(
                startWith(undefined),
                map(() => this.ngForm.submitted),
                distinctUntilChanged(),
            )
            : of(false);

        this.invalid$ = this.ngControl.statusChanges.pipe(
            map($ => $ === 'INVALID'),
            startWith(this.ngControl.status === 'INVALID'),
            distinctUntilChanged(),
        );

        this.dirty$ = this.doCheck$.pipe(
            startWith(void 0),
            map(() => this.ngControl.dirty),
            distinctUntilChanged(),
        );

        this.visible$ = combineLatest([
            this.invalid$,
            this.dirty$,
            this.submitted$,
        ]).pipe(
            map(([$invalid, $dirty, $submitted]: [boolean, boolean, boolean]): boolean => {
                // Specific validation for FieldType.MULTIPLE
                if ('formField' in this.ngControl.valueAccessor && (this.ngControl.valueAccessor as {
                    formField?: FormFieldType
                }).formField.fieldType === FieldType.MULTIPLE) {
                    return $invalid && $submitted;
                }
                return $invalid && ($dirty || $submitted);
            }),
            distinctUntilChanged(),
        )
        ;
    }

    public ngAfterViewInit() {
        this.visible$.pipe(
            takeUntil(this.us$)
        ).subscribe($ => {
            if ($) {
                this.renderer.addClass(this.elementRef.nativeElement, ValidationCssDirective.CLASS_INVALID);
            } else {
                this.renderer.removeClass(this.elementRef.nativeElement, ValidationCssDirective.CLASS_INVALID);
            }
        });

        this.invalid$.pipe(
            takeUntil(this.us$)
        ).subscribe($ => {
            if ($) {
                this.renderer.setAttribute(this.elementRef.nativeElement, ValidationCssDirective.ATTRIBUTE_INVALID, '');
            } else {
                this.renderer.removeAttribute(this.elementRef.nativeElement, ValidationCssDirective.ATTRIBUTE_INVALID);
            }
        });
    }

    public ngDoCheck(): void {
        this.doCheck$.next();
    }

    public ngOnDestroy(): void {
        this.us$.next();
        this.us$.complete();

        this.doCheck$.complete();
    }
}
